import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { resolveColor } from '../../theme/colorTokens.js';
import { sanitizeRichtextHtml } from '../../sanitize/sanitizeRichtextHtml.js';
import { marginFields, visibilityField, weightField, widthField } from '../_shared/blockFields.js';
import { marginStyle, num, readWeight, str, visibilityCss, widthCss, } from '../_shared/blockRender.js';
import { MaterialIcon, MaterialSymbolsFont } from '../_shared/materialSymbols.js';
/**
 * Shrine `text-with-icon` (preset "Text with icon", category "Icons") — config-by-config. A flexible
 * text element for the product information column: up to FOUR short text lines, each prefixed by an
 * optional Google Material Symbols icon (the Shopify model — the merchant types the glyph NAME, e.g.
 * `check_circle`; a custom image overrides it), with extensive styling: responsive desktop/mobile text
 * size, desktop/mobile alignment, solid/gradient text + icon color, icon scale, a block-OR-per-item
 * background (solid/gradient + opacity), border (width/style/solid-or-gradient color/opacity), drop
 * shadow (color/opacity/offset/spread/blur), corner radius, desktop/mobile padding, layout (fit/full
 * width + horizontal/vertical stacking + stacking gap), visibility and margins. It shares the exact icon
 * engine ({@link MaterialIcon}) as `icon-with-text` / `icons-with-content`, and the gradient/color and
 * sanitized-richtext idioms of the `heading` block.
 *
 * SHRINE INVENTORY (67 settings — mapped):
 *  - `visibility` (Display on) → shared `visibilityField` + `visibilityCss`.
 *  Text settings (group 'Text'):
 *   - `mobile_text_size` / `desktop_text_size` (range px) → responsive base/≥750px font-size.
 *   - `alignment` / `mobile_alignment` (left/center/right) → desktop/mobile text-align + flex justify.
 *   - `text_color_type` (solid/gradient) + `text_color` (color) + `text_color_gradient` (gradient) →
 *     solid color OR gradient clipped to the glyphs (`background-clip:text`).
 *   - `font_family` (body/heading) → body vs heading font token.
 *  Icon settings (group 'Icon'):
 *   - `icon_scale` (range %, relative to text size) → icon size = text-size × scale.
 *   - `icon_color_type` (solid/gradient) + `icon_color` (color) + `icon_color_gradient` (gradient) →
 *     solid color OR gradient-clipped glyph (custom image overrides ignore color).
 *  Layout (group 'Layout'):
 *   - `width` (fit-content/100%) → REPLACED (22/09/2026) by the shared `desktopWidth` % slider (`widthField`),
 *     rendered by `widthCss()` exactly like the `text` block. The old `width` value is IGNORED: honouring a
 *     saved "fit-content" at 100 made the block NARROWER at 100 % than at 90 % (founder, 23/09).
 *   - `direction` (horizontal/vertical) → multi-line stacking axis.
 *   - `column_gap` (range) → gap between the stacked items (Shrine `column-gap`/`row-gap` em pair).
 *  Styling (group 'Styling'):
 *   - `styling_container` (container/text) → apply bg/border/shadow/padding to the WHOLE block or EACH item.
 *   - `bg_type` (none/solid/gradient) + `bg_color` + `bg_gradient` + `bg_opacity` (%) → background.
 *   - `border_width` (px) + `border` (solid/dashed) + `border_color_type` (solid/gradient) +
 *     `border_color` + `border_gradient` + `border_opacity` (%) → border (gradient border via padding-box
 *     + gradient border-image).
 *   - `enable_shadow` + `shadow_color` + `shadow_opacity` (%) + `shadow_horizontal_offset` +
 *     `shadow_vertical_offset` + `shadow_spread` + `shadow_blur` → box-shadow.
 *   - `border_radius` (%) → corner radius derived from the rendered item height (Shrine formula).
 *  Padding (group 'Padding'): `vertical_desktop_padding` / `horizontal_desktop_padding` /
 *   `vertical_mobile_padding` / `horizontal_mobile_padding` (px) → responsive padding.
 *  Text & icon #1..#4 (groups 'Text & icon #N'):
 *   - `text_N` (inline richtext) — a line renders only when its text is non-empty (Shrine `text_N != blank`).
 *   - `icon_N` (Material Symbols name) + `filled_icon_N` (FILL axis) + `custom_icon_N` (image override).
 *  Margins: `margin_top` / `margin_bottom` → shared `marginFields` + `marginStyle`.
 *
 * DEFERRED (Shopify-platform-only, documented):
 *  - `block.shopify_attributes` — a Liquid attribute injected by Shopify's section renderer; no schema
 *    control and no equivalent in our block tree.
 *  - preset `category` "Icons" — a Shopify section/block taxonomy label, surfaced via meta, not an
 *    authoring setting.
 *  - The `-new` snippet's `block_width` (fixed/unlimited) + `block_desktop_width`/`block_mobile_width`
 *    px and text_5/text_6 are part of a DIFFERENT (text-with-icon-block-new) variant; the canonical
 *    `text-with-icon` block schema (the 67-setting one mapped above) uses `width` (fit/full) and 4 items.
 *  - `color_extract`/`color_modify`/`divided_by` Liquid filters are reproduced in JS (rgba opacity, the
 *    border-radius height formula, the column/row gap em split) — not deferred, faithfully ported.
 *  - No animation / reveal / cart-AJAX / liquid-app settings exist on this Shrine block, so none deferred.
 */
const ALIGN_OPTS = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
];
const SOLID_GRADIENT_OPTS = [
    { value: 'solid', label: 'Solid' },
    { value: 'gradient', label: 'Gradient' },
];
const DEFAULT_GRADIENT = 'linear-gradient(57deg, rgba(249, 119, 255, 1) 8%, rgba(54, 212, 255, 1) 47%, rgba(43, 252, 135, 1) 83%)';
/** Build the "Text & icon #N" field-set (text + icon name + fill + custom image override). */
function itemFields(n, defText, defKey) {
    const g = `Text & icon #${n}`;
    const textField = `text${n}`;
    return [
        {
            id: textField,
            type: 'richtext',
            label: `Text #${n}`,
            group: g,
            default: { format: 'html', html: defText },
            // Le PLACEHOLDER de contenu, pas une chaîne d'interface : « Texte avec icône » est ce que la
            // vendeuse doit remplacer, pas un mot du logiciel. Sans ce drapeau, `defaultKey` l'excluait
            // du relevé — l'IA écrivait donc la deuxième ligne pendant que la PREMIÈRE gardait
            // « Texte avec icône », publié tel quel au-dessus d'un vrai bénéfice (0059, 25/08).
            ...(defKey ? { defaultKey: defKey, aiWritable: true } : {}),
        },
        {
            id: `icon${n}`,
            type: 'icon-name',
            label: `Icon #${n}`,
            group: g,
            // L'ICÔNE EST UN CHOIX DE SENS, PAS DE STYLE (0059, 25/08). Une coche pour un bénéfice, une
            // flèche de retour pour une garantie, un camion pour la livraison — c'est le texte de la
            // ligne qui commande. Sans ce drapeau, les quatre lignes héritaient de la même coche et la
            // garantie ressemblait à un quatrième bénéfice, sous le bouton d'achat.
            aiConfigurable: true,
            info: 'A Google Material Symbols name (e.g. check_circle). Search names on fonts.google.com/icons.',
            default: 'check_circle',
            placeholder: 'check_circle',
            visibleWhen: { field: textField, notEquals: '' },
        },
        {
            id: `filledIcon${n}`,
            type: 'checkbox',
            label: `Filled icon #${n}`,
            group: g,
            default: false,
            visibleWhen: { field: textField, notEquals: '' },
        },
        {
            id: `customIcon${n}`,
            type: 'image',
            label: `Custom icon #${n} (overrides the icon)`,
            group: g,
            visibleWhen: { field: textField, notEquals: '' },
        },
    ];
}
export const textWithIconBlockSchema = {
    type: 'text-with-icon',
    name: 'Text with icon',
    fields: [
        visibilityField,
        // ── Text settings ──
        {
            id: 'mobileTextSize',
            type: 'range',
            label: 'Mobile text size',
            group: 'Text',
            min: 8,
            max: 24,
            step: 1,
            default: 14,
            unit: 'px',
        },
        {
            id: 'desktopTextSize',
            type: 'range',
            label: 'Desktop text size',
            group: 'Text',
            min: 10,
            max: 30,
            step: 1,
            default: 16,
            unit: 'px',
        },
        {
            id: 'alignment',
            aiConfigurable: true,
            type: 'select',
            label: 'Text alignment',
            group: 'Text',
            default: 'left',
            options: ALIGN_OPTS,
        },
        {
            id: 'mobileAlignment',
            type: 'select',
            label: 'Mobile text alignment',
            group: 'Text',
            default: 'left',
            options: ALIGN_OPTS,
        },
        {
            id: 'textColorType',
            type: 'select',
            label: 'Text color type',
            group: 'Colors',
            default: 'solid',
            options: SOLID_GRADIENT_OPTS,
        },
        {
            id: 'textColor',
            type: 'color',
            label: 'Text color',
            group: 'Colors',
            default: { ref: 'custom', value: '#121212' },
            visibleWhen: { field: 'textColorType', equals: 'solid' },
        },
        {
            id: 'textColorGradient',
            type: 'gradient',
            label: 'Text gradient',
            group: 'Colors',
            default: DEFAULT_GRADIENT,
            visibleWhen: { field: 'textColorType', equals: 'gradient' },
        },
        {
            id: 'fontFamily',
            type: 'select',
            label: 'Font',
            group: 'Text',
            default: 'body',
            options: [
                { value: 'body', label: 'Body' },
                { value: 'heading', label: 'Heading' },
            ],
        },
        { ...weightField, group: 'Text' },
        // ── Icon settings ──
        {
            id: 'iconScale',
            type: 'range',
            label: 'Icon scale',
            group: 'Icon',
            info: 'Relative to text size',
            min: 100,
            max: 200,
            step: 5,
            default: 120,
            unit: '%',
        },
        {
            id: 'iconColorType',
            type: 'select',
            label: 'Icon color type',
            group: 'Icon',
            default: 'solid',
            options: SOLID_GRADIENT_OPTS,
        },
        {
            id: 'iconColor',
            type: 'color',
            label: 'Icon color',
            group: 'Icon',
            default: { ref: 'custom', value: '#121212' },
            visibleWhen: { field: 'iconColorType', equals: 'solid' },
        },
        {
            id: 'iconColorGradient',
            type: 'gradient',
            label: 'Icon gradient',
            group: 'Icon',
            default: DEFAULT_GRADIENT,
            visibleWhen: { field: 'iconColorType', equals: 'gradient' },
        },
        {
            id: 'iconWeight',
            type: 'range',
            label: 'Icon boldness',
            group: 'Icon',
            // Gated to the SOLID icon-color path (like bgOpacity/borderOpacity): boldness is drawn as a
            // text-stroke in the glyph's currentColor, which is transparent under a gradient clip — so it only
            // has a visible effect for a solid icon color.
            info: 'Thickens the icon strokes (0 = default weight). Applies to a solid icon color.',
            min: 0,
            max: 100,
            step: 5,
            default: 0,
            unit: '%',
            visibleWhen: { field: 'iconColorType', equals: 'solid' },
        },
        // ── Layout ──
        // The SAME width control as the `text` block (founder, 22/09/2026: « la largeur doit être configurable
        // par pourcentage exactement comme text ») — a % of the column on desktop, 100 = unchanged, positioned
        // by the desktop alignment. It replaces the old "Fit text / Full" select.
        { ...widthField, group: 'Layout' },
        {
            id: 'direction',
            aiConfigurable: true,
            type: 'select',
            label: 'Stacking direction',
            group: 'Layout',
            info: 'Applied when multiple texts are added.',
            default: 'horizontal',
            options: [
                { value: 'horizontal', label: 'Horizontal' },
                { value: 'vertical', label: 'Vertical' },
            ],
        },
        {
            id: 'columnGap',
            type: 'range',
            label: 'Stacking spacing',
            group: 'Layout',
            min: 0,
            max: 6,
            step: 0.5,
            default: 3,
        },
        // ── Styling ──
        {
            id: 'stylingContainer',
            aiConfigurable: true,
            type: 'select',
            label: 'Apply background, padding, border & shadow to:',
            group: 'Styling',
            default: 'container',
            options: [
                { value: 'container', label: 'Whole block container' },
                { value: 'text', label: 'Each text item' },
            ],
        },
        {
            id: 'bgType',
            type: 'select',
            label: 'Background',
            group: 'Colors',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'solid', label: 'Solid color' },
                { value: 'gradient', label: 'Gradient color' },
            ],
        },
        {
            id: 'bgColor',
            type: 'color',
            label: 'Background color',
            group: 'Colors',
            default: { ref: 'theme', token: 'accent-2' },
            visibleWhen: { field: 'bgType', equals: 'solid' },
        },
        {
            id: 'bgGradient',
            type: 'gradient',
            label: 'Background gradient color',
            group: 'Colors',
            default: DEFAULT_GRADIENT,
            visibleWhen: { field: 'bgType', equals: 'gradient' },
        },
        {
            id: 'bgOpacity',
            type: 'range',
            label: 'Background opacity',
            group: 'Colors',
            min: 0,
            max: 100,
            step: 5,
            default: 100,
            unit: '%',
            // Opacity is folded into the solid rgba() background only; a gradient string is emitted verbatim
            // (no alpha applied), so offer this control solely for the solid path — mirrors `bgColor`'s gate.
            visibleWhen: { field: 'bgType', equals: 'solid' },
        },
        {
            id: 'borderWidth',
            type: 'range',
            label: 'Border thickness',
            group: 'Styling',
            min: 0,
            max: 8,
            step: 1,
            default: 0,
            unit: 'px',
        },
        {
            id: 'border',
            type: 'select',
            label: 'Border style',
            group: 'Styling',
            default: 'solid',
            options: [
                { value: 'solid', label: 'Solid' },
                { value: 'dashed', label: 'Dashed' },
            ],
            visibleWhen: { field: 'borderWidth', notEquals: 0 },
        },
        {
            id: 'borderColorType',
            type: 'select',
            label: 'Border color type',
            group: 'Styling',
            default: 'solid',
            options: SOLID_GRADIENT_OPTS,
            visibleWhen: { field: 'borderWidth', notEquals: 0 },
        },
        {
            id: 'borderColor',
            type: 'color',
            label: 'Border color',
            group: 'Styling',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'borderColorType', equals: 'solid' },
        },
        {
            id: 'borderGradient',
            type: 'gradient',
            label: 'Border gradient color',
            group: 'Styling',
            default: DEFAULT_GRADIENT,
            visibleWhen: { field: 'borderColorType', equals: 'gradient' },
        },
        {
            id: 'borderOpacity',
            type: 'range',
            label: 'Border opacity',
            group: 'Styling',
            min: 0,
            max: 100,
            step: 5,
            default: 100,
            unit: '%',
            // A gradient border is drawn via `border-image` (no alpha applied); opacity only affects the solid
            // rgba() border path, so offer this control solely for the solid color type — mirrors `borderColor`'s gate.
            visibleWhen: { field: 'borderColorType', equals: 'solid' },
        },
        {
            id: 'enableShadow',
            type: 'checkbox',
            label: 'Enable drop shadow',
            group: 'Styling',
            default: false,
        },
        {
            id: 'shadowColor',
            type: 'color',
            label: 'Shadow color',
            group: 'Styling',
            default: { ref: 'custom', value: '#000000' },
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'shadowOpacity',
            type: 'range',
            label: 'Shadow opacity',
            group: 'Styling',
            min: 0,
            max: 100,
            step: 5,
            default: 15,
            unit: '%',
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'shadowHorizontalOffset',
            type: 'range',
            label: 'Shadow horizontal offset (px)',
            group: 'Styling',
            min: -50,
            max: 50,
            step: 2,
            default: 0,
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'shadowVerticalOffset',
            type: 'range',
            label: 'Shadow vertical offset (px)',
            group: 'Styling',
            min: -50,
            max: 50,
            step: 2,
            default: 10,
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'shadowSpread',
            type: 'range',
            label: 'Shadow spread (px)',
            group: 'Styling',
            min: -50,
            max: 50,
            step: 2,
            default: 0,
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'shadowBlur',
            type: 'range',
            label: 'Shadow blur (px)',
            group: 'Styling',
            min: 0,
            max: 100,
            step: 2,
            default: 20,
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'borderRadius',
            type: 'range',
            label: 'Corner radius',
            group: 'Styling',
            min: 0,
            max: 100,
            step: 5,
            default: 50,
            unit: '%',
        },
        // ── Padding ──
        {
            id: 'verticalDesktopPadding',
            type: 'range',
            label: 'Vertical desktop padding',
            group: 'Padding',
            min: 0,
            max: 60,
            step: 3,
            default: 15,
            unit: 'px',
        },
        {
            id: 'horizontalDesktopPadding',
            type: 'range',
            label: 'Horizontal desktop padding',
            group: 'Padding',
            min: 0,
            max: 60,
            step: 3,
            default: 15,
            unit: 'px',
        },
        {
            id: 'verticalMobilePadding',
            type: 'range',
            label: 'Vertical mobile padding',
            group: 'Padding',
            min: 0,
            max: 40,
            step: 2,
            default: 12,
            unit: 'px',
        },
        {
            id: 'horizontalMobilePadding',
            type: 'range',
            label: 'Horizontal mobile padding',
            group: 'Padding',
            min: 0,
            max: 40,
            step: 2,
            default: 12,
            unit: 'px',
        },
        // ── Text & icon #1..#4 ──
        ...itemFields(1, 'Texte avec icône', 'section.text_with_icon'),
        ...itemFields(2, ''),
        ...itemFields(3, ''),
        ...itemFields(4, ''),
        // ── Margins ──
        ...marginFields(15, 15),
    ],
};
/**
 * A stored `color` setting → a CSS colour string. Unlike a bare hex read, this resolves a THEME TOKEN
 * (e.g. "BG"/"INK"/accent) to its `var(--aqx-theme-*)` — so picking a theme colour actually applies (and
 * reflows on re-theme) instead of silently falling back to the literal (founder bug: "BG" not applied).
 * Custom picks return their hex; a bare string / unknown falls back to the literal.
 */
function colorCss(v, fallback) {
    const resolved = resolveColor(v);
    if (resolved)
        return resolved;
    if (typeof v === 'string' && v.length > 0)
        return v;
    return fallback;
}
/**
 * A colour setting rendered WITH opacity. A custom hex → `rgba(r,g,b,a)` (unchanged). A theme token → the
 * `var(--aqx-theme-*)` mixed toward transparent via `color-mix` (a var can't be fed to `rgba()`); at full
 * opacity the var is emitted verbatim. Keeps theme-token backgrounds/borders/shadows honouring opacity.
 */
function alphaColor(v, opacity, fallbackHex) {
    const resolved = resolveColor(v);
    if (resolved && resolved.startsWith('var(')) {
        return opacity >= 1
            ? resolved
            : `color-mix(in srgb, ${resolved} ${Math.round(opacity * 100)}%, transparent)`;
    }
    const hex = resolved ?? (typeof v === 'string' && v.length > 0 ? v : fallbackHex);
    return `rgba(${hexToRgb(hex)},${opacity})`;
}
/** Parse a `#rrggbb`/`#rgb` hex to `r,g,b` (for rgba opacity), defaulting to black on a bad value. */
function hexToRgb(hex) {
    let h = hex.trim().replace('#', '');
    if (h.length === 3) {
        h = h
            .split('')
            .map((c) => c + c)
            .join('');
    }
    const n = Number.parseInt(h, 16);
    if (h.length !== 6 || Number.isNaN(n))
        return '0,0,0';
    return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}
/** Map a Shrine alignment to a CSS text-align keyword. */
function alignKeyword(v, fallback) {
    const s = str(v, fallback);
    return s === 'center' || s === 'right' ? s : 'left';
}
/** Map a CSS alignment to the flex `justify-content` keyword (for the icon+text line). */
function justify(align) {
    if (align === 'center')
        return 'center';
    if (align === 'right')
        return 'flex-end';
    return 'flex-start';
}
/** Resolve the chosen background (none → '', solid → rgba, gradient → string) with the given opacity 0..1. */
function backgroundValue(bs, opacity) {
    const type = str(bs.bgType, 'none');
    if (type === 'solid') {
        return alphaColor(bs.bgColor, opacity, '#BFE3FF');
    }
    if (type === 'gradient') {
        return str(bs.bgGradient, DEFAULT_GRADIENT);
    }
    return '';
}
/**
 * Text-with-icon block — up to four icon + text lines, each rendered only when its text is non-empty
 * (Shrine `text_N != blank`). Background/border/shadow/padding/radius apply either to the whole block
 * container or to each text item (Shrine `styling_container`). Font-size, alignment and padding are
 * responsive at Shrine's 750px breakpoint via BOTH `@media` (live viewport) and `@container aqx-page`
 * (builder device-preview); the icon size is driven through the shared `--aqx-icon-size` var so
 * {@link MaterialIcon} reflows per-breakpoint without per-line markup.
 */
const TextWithIconBlock = ({ block }) => {
    // PRESENTATIONAL block: it renders icons + text from its OWN settings and reads NO product/variant state.
    // It must therefore render anywhere it is placed (image-with-text, rich-text, …), NOT only inside a
    // product section's ProductStateProvider. (A stray `useProductState()` guard here made it silently vanish
    // outside a product section — founder bug 2026-07-26.)
    const bs = block.settings;
    const cls = `aqx-twi-${block.id}`;
    const items = [1, 2, 3, 4]
        .map((n) => {
        const rt = bs[`text${n}`];
        return {
            n,
            html: sanitizeRichtextHtml(rt?.format === 'html' ? rt.html : ''),
            icon: str(bs[`icon${n}`], ''),
            filled: bs[`filledIcon${n}`] === true,
            image: bs[`customIcon${n}`],
        };
    })
        .filter((it) => it.html.trim().length > 0);
    if (items.length === 0)
        return null;
    // ── Text ──
    const mText = num(bs.mobileTextSize, 14);
    const dText = num(bs.desktopTextSize, 16);
    const align = alignKeyword(bs.alignment, 'left');
    const mAlign = alignKeyword(bs.mobileAlignment, 'left');
    const textColorType = str(bs.textColorType, 'solid');
    const fontFamily = str(bs.fontFamily, 'body') === 'heading'
        ? 'var(--aqx-theme-heading-font, var(--aqx-theme-font))'
        : 'var(--aqx-theme-font)';
    // Solid → a flat color; gradient → clip the gradient to the text glyphs.
    const textColorCss = textColorType === 'gradient'
        ? `background:${str(bs.textColorGradient, DEFAULT_GRADIENT)};-webkit-background-clip:text;` +
            `background-clip:text;-webkit-text-fill-color:transparent;color:transparent`
        : `color:${colorCss(bs.textColor, '#121212')}`;
    // Shared "Font weight" — unset ('') emits nothing, keeping the base text rule byte-identical.
    const weight = readWeight(bs);
    const weightCss = weight ? `;font-weight:${weight}` : '';
    // ── Icon ──
    const iconScale = num(bs.iconScale, 120) / 100;
    const iconColorType = str(bs.iconColorType, 'solid');
    const mIcon = Math.round(mText * iconScale);
    const dIcon = Math.round(dText * iconScale);
    // "Icon boldness" 0..100 → an equal text-stroke in EM (the webfont's wght axis is pinned at 400). Em so
    // the stroke scales with the glyph size across breakpoints. A small FLOOR (0.02em) means the very first
    // slider step is already perceptible instead of a sub-pixel no-op; the top of the range reaches ~0.07em.
    const iconWeight = num(bs.iconWeight, 0);
    const iconBoldnessEm = iconWeight > 0 ? Math.round((0.02 + (iconWeight / 100) * 0.05) * 1000) / 1000 : 0;
    // Solid icon → currentColor of a colored wrapper; gradient → clip the gradient to the glyph.
    const iconColorCss = iconColorType === 'gradient'
        ? `background:${str(bs.iconColorGradient, DEFAULT_GRADIENT)};-webkit-background-clip:text;` +
            `background-clip:text;-webkit-text-fill-color:transparent;color:transparent`
        : `color:${colorCss(bs.iconColor, '#121212')}`;
    // ── Layout ──
    // The old "Fit text / Full" select (`bs.width`) is deliberately NOT read: the slider is the only truth,
    // 100 = the whole column, so the box can never be narrower at 100 % than at 90 %.
    const direction = str(bs.direction, 'horizontal');
    const columnGap = num(bs.columnGap, 3);
    // Shrine: --column-gap = column_gap/2 em ; --row-gap = column_gap/5 em.
    const colGapEm = columnGap / 2;
    const rowGapEm = columnGap / 5;
    // ── Styling ──
    const styleOn = str(bs.stylingContainer, 'container'); // 'container' | 'text'
    const bgOpacity = num(bs.bgOpacity, 100) / 100;
    const bg = backgroundValue(bs, bgOpacity);
    const borderWidth = num(bs.borderWidth, 0);
    const borderStyleVal = str(bs.border, 'solid');
    const borderColorType = str(bs.borderColorType, 'solid');
    const borderOpacity = num(bs.borderOpacity, 100) / 100;
    const enableShadow = bs.enableShadow === true;
    // Border: a solid rgba border, or a gradient via border-image (square corners on gradient — like Shrine).
    let borderCss = '';
    if (borderWidth > 0) {
        if (borderColorType === 'gradient') {
            borderCss =
                `border:${borderWidth}px ${borderStyleVal} transparent;` +
                    `border-image:${str(bs.borderGradient, DEFAULT_GRADIENT)} 1`;
        }
        else {
            borderCss = `border:${borderWidth}px ${borderStyleVal} ${alphaColor(bs.borderColor, borderOpacity, 'var(--aqx-theme-accent)')}`;
        }
    }
    // Shadow: offset-x offset-y blur spread color(opacity).
    let shadowCss = '';
    if (enableShadow) {
        const sOpacity = num(bs.shadowOpacity, 15) / 100;
        const ox = num(bs.shadowHorizontalOffset, 0);
        const oy = num(bs.shadowVerticalOffset, 10);
        const blur = num(bs.shadowBlur, 20);
        const spread = num(bs.shadowSpread, 0);
        shadowCss = `box-shadow:${ox}px ${oy}px ${blur}px ${spread}px ${alphaColor(bs.shadowColor, sOpacity, '#000000')}`;
    }
    // Corner radius: Shrine derives px from (text-size + vertical-padding + border-width) × radius% .
    const hasBox = bg !== '' || borderWidth > 0 || enableShadow;
    const radiusPercent = num(bs.borderRadius, 50);
    const vPadD = num(bs.verticalDesktopPadding, 15);
    const hPadD = num(bs.horizontalDesktopPadding, 15);
    const vPadM = num(bs.verticalMobilePadding, 12);
    const hPadM = num(bs.horizontalMobilePadding, 12);
    const radiusD = hasBox ? ((dText + vPadD + borderWidth) * radiusPercent) / 100 : 0;
    const radiusM = hasBox ? ((mText + vPadM + borderWidth) * radiusPercent) / 100 : 0;
    // The box (bg/border/shadow/radius/padding) decoration string — applied to the container OR each item.
    const boxBase = (radius, vPad, hPad) => {
        if (!hasBox)
            return '';
        return (`${bg ? `background:${bg};` : ''}${borderCss ? `${borderCss};` : ''}` +
            `${shadowCss ? `${shadowCss};` : ''}border-radius:${radius}px;padding:${vPad}px ${hPad}px;`);
    };
    const containerBoxM = styleOn === 'container' ? boxBase(radiusM, vPadM, hPadM) : '';
    const containerBoxD = styleOn === 'container' ? boxBase(radiusD, vPadD, hPadD) : '';
    const itemBoxM = styleOn === 'text' ? boxBase(radiusM, vPadM, hPadM) : '';
    const itemBoxD = styleOn === 'text' ? boxBase(radiusD, vPadD, hPadD) : '';
    // Multiple items stack along `direction`; a single item is just one line.
    const containerFlex = `display:flex;flex-direction:${direction === 'vertical' ? 'column' : 'row'};` +
        `flex-wrap:wrap;column-gap:${colGapEm}em;row-gap:${rowGapEm}em;` +
        `inline-size:100%;` +
        `justify-content:${justify(mAlign)};`;
    // Each item = icon + text on one line; the line carries its OWN flex justify so alignment works per line.
    // It also LOCKS its own font-size (not just the container's) so the `gap:0.4em` icon↔text spacing stays
    // relative to the TEXT even if a host theme injects a `p { font-size }` rule (Tailwind typography/prose,
    // a buy-box that enlarges paragraphs, etc.) — a direct rule on `.item` beats that by specificity, whereas
    // inheriting from the container would be overridden by any direct `p` rule.
    const itemLine = `display:flex;align-items:center;gap:0.4em;font-size:${mText}px;--aqx-icon-size:${mIcon}px;` +
        `justify-content:${justify(mAlign)};`;
    // Base (mobile-first) rules. The container AND each item anchor their font-size to the text size so every
    // em-based gap (the item's `gap:0.4em`, plus the container column/row-gap) is relative to the text — NOT
    // to whatever (possibly large) font-size the buy-box column happens to pass down, which would blow the
    // icon↔text spacing wide open.
    const base = `.${cls}{${containerFlex}font-family:${fontFamily};font-size:${mText}px;${containerBoxM}}` +
        `.${cls}__item{${itemLine}${itemBoxM}}` +
        `.${cls}__text{font-size:${mText}px;line-height:var(--aqx-theme-lh-snug, 1.4);${textColorCss}${weightCss}}` +
        `.${cls}__text *{margin:0}` +
        `.${cls}__icon{flex:0 0 auto;line-height:0;${iconColorCss}}`;
    // Desktop (≥750px) overrides: font-size, padding-derived box, alignment, icon size.
    const desktopRule = `.${cls}{justify-content:${justify(align)};font-size:${dText}px;${containerBoxD}}` +
        `.${cls}__item{justify-content:${justify(align)};font-size:${dText}px;--aqx-icon-size:${dIcon}px;${itemBoxD}}` +
        `.${cls}__text{font-size:${dText}px}`;
    const css = base +
        // Desktop override via `@container aqx-page` ONLY (always present via PageRenderer) — a paired
        // @media(min-width) would fire off the builder's wide editor window inside a narrow mobile preview.
        `@container aqx-page (min-width:750px){${desktopRule}}` +
        // Optional desktop width (% of the column), positioned by the desktop alignment; '' at 100%.
        widthCss(cls, bs, align) +
        visibilityCss(cls, bs.visibility);
    const style = marginStyle(bs, 15, 15);
    return (_jsxs(_Fragment, { children: [_jsx(MaterialSymbolsFont, {}), _jsx("style", { dangerouslySetInnerHTML: { __html: css } }), _jsx("div", { className: cls, style: style, children: items.map((it) => (
                // The item MUST NOT be a <p>: its rich text (`it.html`) is itself block content wrapped in a
                // <p> by the editor, and a <p> cannot legally contain another <p>. The browser would auto-close
                // this item at the inner <p>, ejecting the text as a SIBLING — so the container's `column-gap`
                // (not the item's tight `gap`) would end up separating the icon from the text. A <div> nests fine.
                _jsxs("div", { className: `${cls}__item`, children: [_jsx("span", { className: `${cls}__icon`, children: _jsx(MaterialIcon, { name: it.icon, filled: it.filled, image: it.image, size: dIcon, boldness: iconBoldnessEm }) }), _jsx("span", { className: `${cls}__text`, dangerouslySetInnerHTML: { __html: it.html } })] }, it.n))) })] }));
};
export const textWithIconBlock = {
    type: 'text-with-icon',
    schema: textWithIconBlockSchema,
    Component: TextWithIconBlock,
    meta: { label: 'Text with icon' },
};
//# sourceMappingURL=index.js.map